import { useEffect, useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { formatDateBr, formatMonthBr } from '@ana/domain';

/*
 * Date field typed as DD/MM/AAAA (docs/data/IMPORT_ARCHITECTURE.md §9.6) and month field typed as MM/AAAA. The value
 * outside the component stays ISO (AAAA-MM-DD, AAAA-MM), as the API expects; only what the user sees and types is
 * Brazilian, whatever the browser's language. Every date and month field of the screens uses them instead of the
 * browser's own date and month inputs (docs/time/DATETIME_POLICY.md; tests/content/date-fields.unit.test.ts).
 */

/** Digits typed so far → "DD/MM/AAAA" with the slashes placed as the user types. */
export function maskDateBr(text: string): string {
  const d = text.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/**
 * "DD/MM/AAAA" → ISO date, or null when it is not a real calendar date (31/02, 29/02 outside a leap year, month 13).
 * Two-digit years are refused: the century is never guessed.
 */
export function parseDateBr(text: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text.trim());
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const day = Number(dd);
  const month = Number(mm);
  const year = Number(yyyy);
  if (year < 1900 || month < 1 || month > 12 || day < 1) return null;
  const d = new Date(Date.UTC(year, month - 1, day));
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) return null;
  return `${yyyy}-${mm}-${dd}`;
}

export const DATE_BR_MESSAGES = {
  incomplete: 'Informe a data completa no formato DD/MM/AAAA.',
  invalid: 'Data inexistente. Confira o dia e o mês.',
  required: 'Informe a data.',
} as const;

/** Message for a real date outside [min, max] (ISO bounds, either optional); null when it is inside. */
export function dateRangeError(iso: string, min?: string, max?: string): string | null {
  if (!((min && iso < min) || (max && iso > max))) return null;
  const lo = min ? formatDateBr(min) : null;
  const hi = max ? formatDateBr(max) : null;
  if (lo && hi) return `Informe uma data entre ${lo} e ${hi}.`;
  if (lo) return `Informe uma data a partir de ${lo}.`;
  return `Informe uma data até ${hi}.`;
}

export interface DateInputBrProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type' | 'defaultValue' | 'min' | 'max'
> {
  label: string;
  /** ISO date (AAAA-MM-DD) or null when empty. */
  value: string | null;
  /** Called with the ISO date when the text is a real date, and with null when it is emptied or invalid. */
  onChange: (iso: string | null) => void;
  required?: boolean;
  hint?: string;
  /** Hides the label visually (it stays the accessible name). */
  hideLabel?: boolean;
  /**
   * ISO bounds, as the native min/max limited the calendar: a real date outside them is flagged and handed out as
   * null. Left out where the screen reports the range itself.
   */
  min?: string;
  max?: string;
}

interface FieldAria {
  'aria-invalid'?: true;
  'aria-describedby'?: string;
}

/** Shared frame of the two fields: label, text input, hint and error, with the caller's ARIA merged in. */
function BrField(props: {
  inputId: string;
  label: string;
  hideLabel: boolean;
  hint: string | undefined;
  error: string | null;
  externalDescribedBy: string | undefined;
  externalInvalid: InputHTMLAttributes<HTMLInputElement>['aria-invalid'];
  input: (aria: FieldAria) => ReactNode;
}) {
  const { inputId, label, hideLabel, hint, error, externalDescribedBy, externalInvalid } = props;
  const errorId = `${inputId}-erro`;
  const hintId = `${inputId}-dica`;
  const describedBy =
    [externalDescribedBy, hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') ||
    undefined;
  const invalid = error !== null || externalInvalid === true || externalInvalid === 'true';
  const aria: FieldAria = {};
  if (invalid) aria['aria-invalid'] = true;
  if (describedBy) aria['aria-describedby'] = describedBy;
  return (
    <div className="field-wrap">
      <label className="field" htmlFor={inputId}>
        <span className={hideLabel ? 'visually-hidden' : undefined}>{label}</span>
        {props.input(aria)}
      </label>
      {hint ? (
        <span id={hintId} className="small muted">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className="field-error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export function DateInputBr({
  label,
  value,
  onChange,
  required = false,
  hint,
  hideLabel = false,
  min,
  max,
  id,
  onBlur,
  'aria-describedby': externalDescribedBy,
  'aria-invalid': externalInvalid,
  ...rest
}: DateInputBrProps) {
  const autoId = useId();
  const inputId = id ?? `data-${autoId}`;
  const [text, setText] = useState(() => formatDateBr(value) ?? '');
  const [touched, setTouched] = useState(false);

  // A new value from outside (another row, a reset) replaces the text, unless it is the date already typed.
  useEffect(() => {
    setText((current) => (parseDateBr(current) === value ? current : (formatDateBr(value) ?? '')));
  }, [value]);

  const parsed = parseDateBr(text);
  const error =
    text === ''
      ? required && touched
        ? DATE_BR_MESSAGES.required
        : null
      : text.length < 10
        ? touched
          ? DATE_BR_MESSAGES.incomplete
          : null
        : parsed === null
          ? DATE_BR_MESSAGES.invalid
          : dateRangeError(parsed, min, max);

  return (
    <BrField
      inputId={inputId}
      label={label}
      hideLabel={hideLabel}
      hint={hint}
      error={error}
      externalDescribedBy={externalDescribedBy}
      externalInvalid={externalInvalid}
      input={(aria) => (
        <input
          className="date-br"
          {...rest}
          {...aria}
          id={inputId}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="DD/MM/AAAA"
          maxLength={10}
          value={text}
          required={required}
          onChange={(e) => {
            const next = maskDateBr(e.target.value);
            setText(next);
            const iso = parseDateBr(next);
            const accepted = iso !== null && dateRangeError(iso, min, max) ? null : iso;
            if (accepted !== value) onChange(accepted);
          }}
          onBlur={(e) => {
            setTouched(true);
            onBlur?.(e);
          }}
        />
      )}
    />
  );
}

/** Digits typed so far → "MM/AAAA" with the slash placed as the user types. */
export function maskMonthBr(text: string): string {
  const d = text.replace(/\D/g, '').slice(0, 6);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}/${d.slice(2)}`;
}

/** "MM/AAAA" → ISO month (AAAA-MM), or null for month 00 or 13 and up, and for two-digit years. */
export function parseMonthBr(text: string): string | null {
  const m = /^(\d{2})\/(\d{4})$/.exec(text.trim());
  if (!m) return null;
  const [, mm, yyyy] = m;
  const month = Number(mm);
  if (Number(yyyy) < 1900 || month < 1 || month > 12) return null;
  return `${yyyy}-${mm}`;
}

export const MONTH_BR_MESSAGES = {
  incomplete: 'Informe o mês completo no formato MM/AAAA.',
  invalid: 'Mês inexistente. Use de 01 a 12.',
  required: 'Informe o mês.',
} as const;

export type MonthInputBrProps = Omit<DateInputBrProps, 'min' | 'max'>;

/** Month field typed as MM/AAAA; the value outside stays ISO (AAAA-MM). Same behaviour as DateInputBr. */
export function MonthInputBr({
  label,
  value,
  onChange,
  required = false,
  hint,
  hideLabel = false,
  id,
  onBlur,
  'aria-describedby': externalDescribedBy,
  'aria-invalid': externalInvalid,
  ...rest
}: MonthInputBrProps) {
  const autoId = useId();
  const inputId = id ?? `mes-${autoId}`;
  const [text, setText] = useState(() => formatMonthBr(value) ?? '');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setText((current) => (parseMonthBr(current) === value ? current : (formatMonthBr(value) ?? '')));
  }, [value]);

  const error =
    text === ''
      ? required && touched
        ? MONTH_BR_MESSAGES.required
        : null
      : text.length < 7
        ? touched
          ? MONTH_BR_MESSAGES.incomplete
          : null
        : parseMonthBr(text) === null
          ? MONTH_BR_MESSAGES.invalid
          : null;

  return (
    <BrField
      inputId={inputId}
      label={label}
      hideLabel={hideLabel}
      hint={hint}
      error={error}
      externalDescribedBy={externalDescribedBy}
      externalInvalid={externalInvalid}
      input={(aria) => (
        <input
          className="month-br"
          {...rest}
          {...aria}
          id={inputId}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="MM/AAAA"
          maxLength={7}
          value={text}
          required={required}
          onChange={(e) => {
            const next = maskMonthBr(e.target.value);
            setText(next);
            const iso = parseMonthBr(next);
            if (iso !== value) onChange(iso);
          }}
          onBlur={(e) => {
            setTouched(true);
            onBlur?.(e);
          }}
        />
      )}
    />
  );
}
